import {
  isCatProfile,
  isDogProfile,
  PetProfile,
  DogProfile,
  CatProfile,
} from 'types/pet-profile';
import {
  BasePet,
  CatPet,
  CreatePetPayload,
  DogPet,
  Pet,
  UpdatePetPayload,
} from 'api/pets';
import { INITIAL_CAT_STATE, INITIAL_DOG_STATE } from 'stores/pet-profiles';
import isEqual from 'lodash/isEqual';
import { PetType } from 'constants/pet-type';

type generatePetProfileArgs = {
  petProfile: PetProfile;
  id?: string;
  customerId?: string;
};

/**
 * Helper function for toPayloadByPetProfile
 */
export const toPayloadBase = ({
  petProfile,
  id,
  customerId,
}: generatePetProfileArgs): BasePet => {
  return {
    ...(id ? { id } : {}),
    ...(customerId ? { customer_id: customerId } : {}),
    pet_type: petProfile.petType,
    name: petProfile.name || '',
    age: petProfile.selectedAge ?? null,
    needs: petProfile.selectedNeeds,
    allergies: petProfile.selectedAllergies,
    food_types: petProfile.selectedFoodTypes,
    weight: petProfile.selectedWeight,
  };
};

/**
 * It converts petProfile into base payload for the requests such as CreatePetPayload or UpdatePetPayload
 */
export const toPayloadByPetProfile = ({
  petProfile,
  id,
  customerId,
}: generatePetProfileArgs): CreatePetPayload | UpdatePetPayload => {
  const base = toPayloadBase({ petProfile, id, customerId });

  if (isDogProfile(petProfile)) {
    return {
      ...base,
      pet_type: 'dog',
      breed: petProfile.selectedBreed,
    };
  }

  if (isCatProfile(petProfile)) {
    return {
      ...base,
      pet_type: 'cat',
      wet_food_tags: petProfile.selectedWetFoodTypes || [],
      protein_tags: petProfile.selectedTastePreferences || [],
    };
  }

  throw new Error('Invalid profile type');
};

/**
 * Overload type for fromPetToProfile
 */
export function fromPetToProfile(pet: DogPet): DogProfile;
export function fromPetToProfile(pet: CatPet): CatProfile;
export function fromPetToProfile(pet: Pet): PetProfile;
/**
 * It converts Pet to petProfile.
 * It returns the CatProfile or DogProfile regarding the pet_type value, or it returns PetProfile in case of Pet type
 */
export function fromPetToProfile(pet: Pet): PetProfile {
  const base = {
    id: pet.id,
    name: pet.name || '',
    petType: pet.pet_type,
    selectedAge: pet.age ?? undefined,
    selectedAllergies: pet.allergies,
    selectedFoodTypes: pet.food_types,
    selectedNeeds: pet.needs,
    selectedWeight: pet.weight,
    updatedAt: pet.updated_at,
    lastTouched: undefined,
  };

  if (pet.pet_type === PetType.Dog) {
    return {
      ...base,
      petType: pet.pet_type,
      selectedBreed: pet.breed ?? undefined,
    };
  }

  if (pet.pet_type === PetType.Cat) {
    return {
      ...base,
      petType: pet.pet_type,
      selectedWetFoodTypes: pet.wet_food_tags || [],
      selectedTastePreferences: pet.protein_tags || [],
    };
  }

  throw new Error('Invalid profile type');
}

/**
 * Checks if it is equal to init state. If it is not, it means user made a change
 */
export const isInitialProfile = (petProfile: PetProfile): boolean => {
  if (isDogProfile(petProfile)) {
    return isEqual(petProfile, INITIAL_DOG_STATE);
  }

  if (isCatProfile(petProfile)) {
    return isEqual(petProfile, INITIAL_CAT_STATE);
  }

  return false;
};
